import { UploadId } from '@deltasierra/ids';
import { BuilderTemplateId } from '../builderTemplate';
import { FullAuditable, HasId, Omit, ProtocolRelativeUrl, SignedUrl, ApiRoute, HttpMethod } from '../common';
import { LocationDraftId } from '../locationDraft';
import { PublishedArtifactId } from '../publishedArtifact';

export enum CopyrightStatus {
    Unknown = 'unknown',
    Failed = 'failed',
    Skipped = 'skipped',
    Clean = 'clean',
}

export { UploadId };

export interface Upload extends HasId<UploadId>, FullAuditable {
    filename: string;
    contentType: string;
    isImage: boolean;
    key: string;
    url: ProtocolRelativeUrl | null;
    ext: string;
    size: number | null;
    width: number | null;
    height: number | null;
    thumb256x256key: string | null;
    thumb256x256url: ProtocolRelativeUrl | null;
    plannerId: number | null;
    reportId: number | null;
    tryThisId: number | null;
    builderTemplateId: BuilderTemplateId | null;
    locationDraftId: LocationDraftId | null;
    multiImagePublishedArtifactId: PublishedArtifactId | null;
    savedEmailPartId: number | null;
    commentId: number | null;
    status: string;
    copyrightStatus: CopyrightStatus;
    category: UploadCategory | null;
    graphqlId: string;
}

export type UploadWithSignedUrls = Omit<Upload, 'thumb256x256url' | 'url'> & {
    url: SignedUrl | null;
    thumb256x256url: SignedUrl | null;
};

export interface GalleryDetailsUpload {
    filename: string;
    thumb256x256url: string | null;
    url: string;
    isImage: boolean;
    ext: string;
}

export interface InitialUpload {
    category: UploadCategory;
    fileName: string;
    fileType: string;
    width: number | null;
    height: number | null;
    size: number | null;
}

export interface InitialUploadResponse {
    upload: Upload;
    signedUrl: SignedUrl;
}

export interface FinalizeThumbnailRequest {
    uploadId: UploadId;
    thumbnailId: UploadId;
}

export interface FinalizeThumbnailResponse {
    upload: Upload;
    thumbnail: Upload;
}

export type UploadCategory =
    // Used for admin related uploads e.g. manually generated reports
    | 'admin'
    | 'assetLibrary'
    | 'buildableMediaTemplateImage'
    | 'builderTemplate'
    | 'builderTemplateImage'
    | 'campaignMonitorEmailUserReport'
    | 'campaigns/planner'
    | 'comment'
    | 'emailHtml'
    | 'emailPublishing/emailList'
    | 'emailResources'
    | 'emailTemplateImage'
    | 'font'
    | 'htmlScreenshot'
    | 'imageConversion'
    | 'local-media-publishes'
    | 'locationCategoryOptionLogo'
    | 'locationDetails'
    | 'logo'
    | 'map'
    | 'planner'
    | 'profilePicture'
    // For instagram mobile app publish flow
    | 'public/instagram'
    | 'publishedArtifacts'
    | 'publishedArtifacts/print'
    | 'sendgridEmailUserReport'
    | 'specialRequest'
    | 'suppressedEmailsReport'
    // Used for assigning uploads to resources tracked in our DB from external sources
    | 'thirdPartyResource'
    | 'toListUploads'
    | 'trythis'
    | 'trythis/thumbnail'
    | 'videoClip'
    | 'videoOverlay'
    | 'videos';

export type UploadThumbnail = Pick<Upload, 'thumb256x256url'>;

export interface BaseUploadParams {
    id: UploadId;
}

export interface GetThumbnailQuery {
    key: string;
}

export const UploadApi = {
    finalizeThumbnail: new ApiRoute<FinalizeThumbnailResponse, void, FinalizeThumbnailRequest, void>(
        HttpMethod.Post,
        path => path.literal('uploads').literal('thumbnail'),
    ),
    getUploadThumbnail: new ApiRoute<UploadThumbnail, BaseUploadParams, void, GetThumbnailQuery>(HttpMethod.Get, path =>
        path.literal('uploads').param('id'),
    ),
    initUpload: new ApiRoute<InitialUploadResponse, void, InitialUpload, void>(HttpMethod.Post, path =>
        path.literal('uploads'),
    ),
    uploadFailure: new ApiRoute<Upload, BaseUploadParams, void, void>(HttpMethod.Post, path =>
        path.literal('uploads').param('id').literal('fail'),
    ),
    uploadSuccess: new ApiRoute<Upload, BaseUploadParams, void, void>(HttpMethod.Post, path =>
        path.literal('uploads').param('id').literal('succeed'),
    ),
};
